import { translate } from './translate.js';
const BULK_NAMES_SHOWN = 3;
function quote(name) {
    return `“${name}”`;
}
function joinNames(locale, names) {
    const shown = names.slice(0, BULK_NAMES_SHOWN).map(quote).join(', ');
    const remaining = names.length - BULK_NAMES_SHOWN;
    if (remaining <= 0)
        return shown;
    return `${shown} ${translate(locale, 'common:asset.bulk.andMore', { count: remaining })}`;
}
/**
 * `assetName` is REQUIRED — no overload omits it, so a generic
 * "this"/"the item" toast/confirm (a review block, CODE_GUIDELINES §16.1)
 * cannot be written. A single name renders quoted; an array renders a
 * plural count + up to 3 quoted names + "and N more".
 */
function describeAsset(locale, assetName) {
    if (typeof assetName === 'string')
        return quote(assetName);
    if (assetName.length === 0) {
        throw new RangeError('assetName must be a non-empty string or a non-empty array of names');
    }
    if (assetName.length === 1)
        return quote(assetName[0]);
    const count = translate(locale, 'common:asset.bulk.count', { count: assetName.length });
    return `${count} — ${joinNames(locale, assetName)}`;
}
/**
 * Builds a render-ready toast payload that always names the asset acted on
 * (§16.1), e.g. `assetToast('en', 'success', 'Deleted', doc.title)` →
 * `{ tone: 'success', message: 'Deleted "Quantum Mechanics 101".' }`.
 * `action` is caller-supplied localized copy (already resolved through
 * `t()` at the call site, e.g. `t('common:toast.deleted.action')`); this
 * helper only owns the asset-naming contract, not every possible verb.
 */
export function assetToast(locale, tone, action, assetName) {
    return { tone, message: `${action} ${describeAsset(locale, assetName)}.` };
}
/**
 * Builds a render-ready confirm-dialog payload, e.g.
 * `assetConfirm('en', 'Delete', doc.title, { destructive: true })` →
 * `{ message: 'Delete "Quantum Mechanics 101"? This can’t be undone.', destructive: true }`.
 */
export function assetConfirm(locale, action, assetName, opts) {
    const destructive = opts?.destructive ?? false;
    const base = `${action} ${describeAsset(locale, assetName)}?`;
    const message = destructive ? `${base} ${translate(locale, 'common:state.confirmDestructive')}` : base;
    return { message, destructive };
}
